// apps/web/src/routes/$lang/_layout/category.$category.$city.tsx
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { transportCategoryRedirect } from "../../../features/transport/lib/hub-redirect";
import { parseLang } from "@majourneys/shared";
import { ExplorePage } from "../../../features/activities/pages/explore-page";
import {
  activitiesQueryOptions,
  buildActivityFilters,
  parseShowFilter,
  type ActivityRouteSearch,
} from "../../../features/activities/api/activities-query";
import {
  categoryBySlugQueryOptions,
  cityBySlugQueryOptions,
} from "../../../features/activities/hooks/use-seo-lookup";
import { cityDisplayName } from "../../../features/activities/lib/city-display-name";
import { cityArticlesQueryOptions } from "../../../features/blog/hooks/use-city-articles";
import { isUnionHubSlug } from "../../../features/activities/lib/explore-segments";
import { prefetchEmptyHubRail } from "../../../features/activities/api/empty-hub-prefetch";
import { isEmptyCityHubView, listTotal } from "../../../features/activities/lib/nearby-bookable-cities";
import { ensureHubSlugsExist } from "../../../features/activities/api/hub-guard";
import { categoriesQueryOptions } from "../../../features/activities/hooks/use-categories";
import { citiesQueryOptions } from "../../../features/activities/hooks/use-cities";
import { ALL_STAY_TYPES, stayTypeQueryOptions } from "../../../features/stays/api/stays-query";
import { parseChildAges } from "../../../features/stays/lib/nights";
import { canonicalCitySlug } from "../../../features/activities/lib/city-aliases";

/** The activity params plus the dates and party the Stays hub prices for. */
export interface CityHubSearch extends ActivityRouteSearch {
  checkin?: string;
  checkout?: string;
  adults?: number | string;
  childAges?: string;
}

export const Route = createFileRoute("/$lang/_layout/category/$category/$city")({
  // Transfers are booked on the transport hub only; the category page 301s there.
  beforeLoad: ({ params }) => {
    const hub = transportCategoryRedirect(params.category, params.lang);
    if (hub) throw redirect(hub);
    // Another spelling of the city (fes, tanger): 301 to the real slug, query kept.
    const city = canonicalCitySlug(params.city);
    if (city) {
      throw redirect({
        to: "/$lang/category/$category/$city",
        params: { lang: params.lang, category: params.category, city },
        search: (prev) => prev,
        statusCode: 301,
      });
    }
  },
  // See place.$city.tsx — prefetch so navigation keeps the old page visible
  // rather than flashing the full-page Suspense spinner.
  loaderDeps: ({ search }: { search: CityHubSearch }) => ({
    q: search.q,
    page: search.page,
    min_price: search.min_price,
    max_price: search.max_price,
    sort: search.sort,
    show: search.show,
    tag: search.tag,
    cuisine: search.cuisine,
    // The booking bar and the "Open now" / "Tables tonight" chips filter the
    // list, so they key the prefetch too (see buildActivityFilters).
    date: search.date,
    time: search.time,
    open: search.open,
    tonight: search.tonight,
    checkin: search.checkin,
    checkout: search.checkout,
    adults: search.adults,
    childAges: search.childAges,
  }),
  loader: async ({ context, params, deps }) => {
    // An unknown city or category is a 404, not an empty page (hub-guard.ts).
    await ensureHubSlugsExist(context.queryClient, { city: params.city, category: params.category });
    const filters = buildActivityFilters(params, deps);
    const [listResponse, cityRow] = await Promise.all([
      context.queryClient.ensureQueryData(activitiesQueryOptions(filters)),
      context.queryClient.ensureQueryData(cityBySlugQueryOptions(params.city)),
      context.queryClient.ensureQueryData(categoryBySlugQueryOptions(params.category)),
      // The cross-links read these to link only hubs with something in them.
      context.queryClient.ensureQueryData(categoriesQueryOptions(params.city, params.lang)),
      context.queryClient.ensureQueryData(citiesQueryOptions(params.lang)),
      warmCityStays(context.queryClient, params, deps),
    ]);
    // Nothing in this category here: warm the "book nearby" rail, which
    // starts with the city's own other activities. See place.$city.tsx.
    if (
      isEmptyCityHubView({
        city: params.city,
        category: params.category,
        total: listTotal(listResponse),
        q: deps.q,
        minPrice: deps.min_price,
        maxPrice: deps.max_price,
        // The page reads `show` on union hubs only; match it exactly.
        show: isUnionHubSlug(params.category) ? parseShowFilter(deps.show) : undefined,
      })
    ) {
      await prefetchEmptyHubRail(context.queryClient, {
        citySlug: params.city,
        lang: params.lang ?? "en",
        includeSelf: true,
      });
    }
    // Prefetch "Read about {city}" so the article anchors reach the SSR HTML —
    // see place.$city.tsx for the full rationale. Best-effort only.
    const lang = parseLang(params.lang);
    await context.queryClient
      .ensureQueryData(
        cityArticlesQueryOptions(
          lang,
          cityDisplayName(cityRow, lang, params.city),
        ),
      )
      .catch(() => undefined);
  },
  component: ExplorePage,
});

/**
 * The city's hotels, riads and villas on a Stays hub, fetched before render.
 *
 * Without this the server HTML held only the camps: the hotel cards were
 * fetched in the browser, so a crawler saw a Marrakech Stays page with no
 * hotels on it, and the page marked itself noindex whenever the city had no
 * camp (Search Console, 2026-10-09).
 *
 * The options are built exactly as StaysHubGrid and ExplorePage build theirs
 * (same pricing params, childAges undefined when there are none, no currency
 * or nationality, which only enter the key once dates are picked), so the
 * page reads this entry rather than running the supplier lookup again.
 *
 * Awaited on the server only, as on category.$category.tsx: in the browser an
 * awaited loader would hold the old page on screen until the supplier answers.
 * The undated list is also warmed on a dated search, because the type tiles
 * count from it.
 */
async function warmCityStays(
  queryClient: QueryClient,
  params: { lang?: string; category: string; city: string },
  deps: { checkin?: string; checkout?: string; adults?: number | string; childAges?: string },
) {
  if (params.category !== "stays") return;
  const lang = params.lang ?? "en";
  const adults = deps.adults != null && deps.adults !== "" ? Number(deps.adults) : undefined;
  const childAges = parseChildAges(deps.childAges);
  const pricing = {
    checkin: deps.checkin,
    checkout: deps.checkout,
    adults,
    childAges: childAges.length ? childAges : undefined,
  };
  const warm = (p: typeof pricing | Record<string, never>) =>
    queryClient.ensureQueryData(stayTypeQueryOptions(ALL_STAY_TYPES, lang, p, params.city)).catch(() => undefined);
  const dated = !!(deps.checkin || deps.checkout || adults != null || pricing.childAges);
  const jobs = dated ? [warm(pricing), warm({})] : [warm({})];
  if (typeof window === "undefined") await Promise.all(jobs);
}
